import React, { useState, useEffect } from "react";
import {useLocation, useNavigate} from "react-router-dom";
import "../components/styles/OrderPayment.css";
import PaymentPopup from "../components/payment/PaymentPopup";
import {error} from "next/dist/build/output/log";
import { toast } from "../utils/toast";

const OrderPayment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const orderInfo = location.state?.orderInfo;
    const [orderData, setOrderData] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState("간편결제");
    const [savePayment, setSavePayment] = useState(true);
    const [usedPoints, setUsedPoints] = useState("");
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [email, setEmail] = useState("");
    const [showEmailInput, setShowEmailInput] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [bankTransferInfo, setBankTransferInfo] = useState(null); // 가상 계좌 정보 저장
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [maxPoints, setMaxPoints] = useState(0); // 사용 가능 포인트
    const [discountAmount, setDiscountAmount] = useState(0);
    const [isOrderUpdated, setIsOrderUpdated] = useState(false); // ✅ 주문 업데이트 완료 여부
    const [isFirstLoading, setIsFirstLoading] = useState(false);
    const [firstPrice, setFirstPrice] = useState(0);

    const paymentMethods = {
      "간편결제" : "SIMPLE_PAY",
        // "카드결제" : "CARD",
        "무통장 입금" : "BANK_TRANSFER",
    };

    useEffect(() => {
        // ✅ 주문 업데이트 데이터
        setIsFirstLoading(true);

        // ✅ 주문 정보 조회
        fetch("/api/ordersheet")
            .then(res => res.json())
            .then(data => {
                if (!data.orderId) {
                    toast("결제할 주문이 없습니다. 장바구니에서 상품을 추가해주세요.");
                    navigate("/cart");
                    return;
                }

                if (data.usePoint > 0 || data.couponId) {
                    toast("잘못된 접근입니다. 주문 정보를 다시 확인해주세요.");
                    navigate("/cart");
                }
                setFirstPrice(data.totalSum.toLocaleString());
                setOrderData(data);
                // 포인트는 10원 단위로 사용 가능
                setMaxPoints(data.memberPoint - (data.memberPoint % 10));
            })
            .catch((reason) => {
                console.log("주문 정보 조회 실패:", reason);
                toast("주문 정보를 불러오는 데 실패했습니다.");
                navigate("/cart");
            });

        // ✅ 사용 가능 쿠폰 조회
        fetch("/api/coupon")
            .then(res => res.json())
            .then(data => {
                setAvailableCoupons(data.availableCoupons || []);
            })
            .catch(() => {
                setAvailableCoupons([]);
            });
    }, [navigate]);

    // ✅ 최종 결제 금액 계산
    let finalPrice;
    finalPrice = 0;
    if (isFirstLoading) {
         finalPrice = Math.max(
            orderData ? orderData.totalSum - (parseInt(usedPoints, 10) || 0) - discountAmount : 0,
            0
        );
    } else {
        finalPrice = Math.max(orderData ? orderData.totalSum : 0, 0);
    }

    if (!orderData) return <p>주문 정보를 불러오는 중...</p>;

    const handlePaymentSelect = (method) => {
        setSelectedPayment(method);
        if (method === "무통장 입금") {
            setShowEmailInput(true);
            setIsPopupOpen(true);
        } else {
            setShowEmailInput(false);
            setEmail("");
            setEmailError("");
        }
    };

    // ✅ 무통장 입금 처리
    const handleBankTransfer = async () => {
        setIsFirstLoading(false);

        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            setEmailError("유효한 이메일을 입력하세요.");
            return;
        }

        const bankTransferData = {
            orderId: orderData.orderId,
            orderEmail: email,
        };

        toast("가상 계좌가 발급되었습니다. 입력하신 이메일을 준비해주세요.\n 📌 기업은행: 001-139191-01-018\n📌 우리은행: 1005-204-707641\n예금주: 박찬엽(소프트캣)");

        try {
            const response = await fetch(`/api/order/bank-transfer`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(
                    bankTransferData,
                ),
            });

            if (!response.ok) throw new Error("무통장 입금 요청 실패");

            const result = await response.json();
            // setBankTransferInfo(result); // 가상 계좌 정보 저장
            if (result.result === "success") {
                navigate("/order/success");
            } else if (result.result === "no-email") {
                setEmailError("유효한 이메일을 입력하세요.");
            } else {
                toast("무통장 입금 요청 중 오류가 발생했습니다.");
            }

        } catch (error) {
            console.error("무통장 입금 요청 실패:", error);
            toast("입금 요청 중 오류가 발생했습니다.");
        }
    };

    // ✅ **주문서 업데이트 (결제 버튼 클릭 시 실행)**
    const handleUpdateOrderSheet = async () => {
        setIsFirstLoading(false);
        try {
            const orderUpdateData = {
                orderId : orderData.orderId,
                usePoint: usedPoints ? parseInt(usedPoints, 10) : 0,
                couponId: selectedCoupon?.couponId || null,
                paymentMethod : paymentMethods[selectedPayment],
            };

            const updateResponse = await fetch("/api/ordersheet", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(orderUpdateData),
            });

            const updatedOrderData = await updateResponse.json();
            if (!updateResponse.ok) throw new Error("주문서 업데이트 실패");

            setOrderData(updatedOrderData);
            setIsOrderUpdated(true); // ✅ 주문 업데이트 완료 플래그 설정

            return updatedOrderData;
        } catch (error) {
            console.error("주문서 업데이트 실패:", error);
            toast("주문 정보를 업데이트하는 데 실패했습니다.");
            return null;
        }
    };

    // ✅ 결제 진행
    const handleCheckoutOrderExpress = async () => {
        console.log("주문 정보:", orderInfo);
        try{
            // ✅ **주문 정보 업데이트 후 결제 진행**
            const updatedOrder = await handleUpdateOrderSheet();
            if (!updatedOrder) return;

            // 결제 금액이 0원인 경우 결제 검증 API 직접 호출
            if (finalPrice === 0) {
                const requestBody = {
                    impUid: "free-" + crypto.randomUUID(),
                    merchantUid: "free-" + crypto.randomUUID(),
                    paymentId: "free-" + crypto.randomUUID(),
                    amount: 0,
                    orderId: orderData.orderId,
                };

                // 서버로 결제 검증 요청 전송
                const verifyResponse = await fetch(`/api/payment/verify`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody),
                });

                if (verifyResponse.status === 200) {
                    toast('결제가 완료되었습니다.');
                    navigate("/order/success");
                } else {
                    toast('결제 검증을 실패했습니다. 주문서를 완전히 나간 후, 다시 시도해주세요.');
                    navigate("/order/failed");
                }
                return;
            }

            let IMP = window.IMP;
            if (!IMP) {
                toast("PortOne 결제 모듈이 로드되지 않았습니다. 새로고침 후 다시 시도해주세요.");
                return;
            }

            IMP.init("imp32683681"); // 예: 'imp00000000'        // console.log("IMP:", IMP);
            // ✅ 결제 요청 (PortOne / 나이스페이먼츠)
            const paymentData = {
                pg: "nice_v2", // ✅ 꼭 'nice'로 명시해야 실제 NICE 결제 모듈 연동됨
                storeId: "store-53a71569-850d-4d9e-8525-766176ab70c3",
                channelKey: "channel-key-612753d2-6ec7-4e02-bb25-c050c0672f79",
                pay_method: "card",
                merchant_uid: `payment-${crypto.randomUUID()}`, // 주문번호
                name: orderData.orderItems.map(item => item.name).join(", "), // 상품명
                amount: finalPrice || 0, // 결제 금액
                buyer_email: orderData.email || '',
                buyer_name: orderData.name || '',
                buyer_tel: orderData.phoneNumber || '',
                buyer_addr: null,
                buyer_postcode: null,
            };

            console.log("결제 요청 데이터:", paymentData);

            // 나이스 페이먼츠 결제창 호출
            IMP.request_pay(paymentData, async function (rsp) {
                if (rsp.error_code != null) {
                    toast(`결제에 실패하였습니다. 다시 시도해주세요. 에러 내용: ${rsp.error_msg}`);
                    navigate("/cart");
                }
                else {
                    console.log('결제 성공:', rsp);

                    // 결제 성공 후 서버로 검증 요청
                    const requestBody = {
                        impUid: rsp.imp_uid, // 결제 고유번호
                        merchantUid: rsp.merchant_uid, // 주문번호
                        paymentId: rsp.merchant_uid, // 결제번호
                        amount: paymentData.amount, // 결제 금액
                        orderId: orderData.orderId, // 주문번호
                    };

                    // 서버로 결제 검증 요청 전송
                    const verifyResponse = await fetch(`/api/payment/verify`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(requestBody),
                    });

                    var redirectUrl = `http://localhost:3000/order/success`;
                    if (verifyResponse.status === 200) {
                        toast('결제가 완료되었습니다.');
                        navigate("/order/success");
                    } else {
                        toast('결제 검증을 실패했습니다. 주문서를 완전히 나간 후, 다시 시도해주세요.');
                        navigate("/order/failed")
                    }
                }
            });
        } catch (error) {
            console.error('결제 실패:', error);
            toast(`결제에 실패했습니다. 다시 시도해주세요.`);

            // 포인트 쿠폰 초기화
            navigate("/cart");
        }
    };

    // ✅ 결제 요청 (나이스페이먼츠)
    // ✅ PortOne 결제 요청
    const handleCheckout = async () => {
        try{
            // ✅ **주문 정보 업데이트 후 결제 진행**
            const updatedOrder = await handleUpdateOrderSheet();
            if (!updatedOrder) return;

            // 결제 금액이 0원인 경우 결제 검증 API 직접 호출
            if (finalPrice === 0) {
                const requestBody = {
                    impUid: "free-" + crypto.randomUUID(),
                    merchantUid: "free-" + crypto.randomUUID(),
                    paymentId: "free-" + crypto.randomUUID(),
                    amount: 0,
                    orderId: orderData.orderId,
                };

                // 서버로 결제 검증 요청 전송
                const verifyResponse = await fetch(`/api/payment/verify`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody),
                });

                if (verifyResponse.status === 200) {
                    toast('결제가 완료되었습니다.');
                    navigate("/order/success");
                } else {
                    toast('결제 검증을 실패했습니다. 주문서를 완전히 나간 후, 다시 시도해주세요.');
                    navigate("/order/failed");
                }
                return;
            }

            let IMP = window.IMP;
            if (!IMP) {
                toast("PortOne 결제 모듈이 로드되지 않았습니다. 새로고침 후 다시 시도해주세요.");
                return;
            }

            IMP.init("imp32683681"); // 예: 'imp00000000'        // console.log("IMP:", IMP);

            // 💳 PortOne 결제 요청 데이터
            const paymentData = {
                pg: "nice_v2", // ✅ 꼭 'nice'로 명시해야 실제 NICE 결제 모듈 연동됨
                storeId: "store-53a71569-850d-4d9e-8525-766176ab70c3", // ✅ 스토어 ID
                channelKey: "channel-key-612753d2-6ec7-4e02-bb25-c050c0672f79", // ✅ 채널 키
                pay_method: 'card',
                merchant_uid: `payment-${crypto.randomUUID()}`, // 주문번호
                name: orderData.orderItems.map(item => item.name).join(", "), // 상품명
                amount: finalPrice || 0, // 결제 금액
                buyer_email: orderData.email || '',
                buyer_name: orderData.name || '',
                buyer_tel: orderData.phoneNumber || '',
                buyer_addr: null,
                buyer_postcode: null,
            };

            console.log('나이스 페이먼츠 결제 요청 데이터:', paymentData);

            // 나이스 페이먼츠 결제창 호출
            IMP.request_pay(paymentData, async function (rsp) {
                if (rsp.error_code != null) {
                    toast(`결제에 실패하였습니다. 다시 시도해주세요. 에러 내용: ${rsp.error_msg}`);
                    navigate("/cart");
                }
                else {
                    console.log('결제 성공:', rsp);

                    // 결제 성공 후 서버로 검증 요청
                    const requestBody = {
                        impUid: rsp.imp_uid, // 결제 고유번호
                        merchantUid: rsp.merchant_uid, // 주문번호
                        paymentId: rsp.merchant_uid, // 결제번호
                        amount: paymentData.amount, // 결제 금액
                        orderId: orderData.orderId, // 주문번호
                    };

                    // 서버로 결제 검증 요청 전송
                    const verifyResponse = await fetch(`/api/payment/verify`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(requestBody),
                    });

                    var redirectUrl = `http://localhost:3000/order/success`;
                    if (verifyResponse.status === 200) {
                        toast('결제가 완료되었습니다.');
                        navigate("/order/success")
                    } else {
                        toast('결제 검증을 실패했습니다. 주문서를 완전히 나간 후, 다시 시도해주세요.');
                        navigate("/order/failed")
                    }
                }
            });
        } catch (error) {
            console.error('결제 실패:', error);
            toast(`결제에 실패했습니다. 다시 시도해주세요.`);

            // 포인트 쿠폰 초기화
            navigate("/cart");
        }
    }

    // ✅ 포인트 입력 핸들러
    const handlePointChange = (e) => {
        let value = e.target.value.replace(/\D/g, ""); // 숫자만 입력
        value = Math.min(Number(value), maxPoints, orderData.totalSum); // 사용 가능 포인트 내에서만 입력 가능
        setUsedPoints(value);
    };

    // 포인트 10원 단위로 설정
    const handlePointChangeDigit = (e) => {
        let value = Math.floor(e.target.value / 10) * 10;
        setUsedPoints(value);
    }

    // ✅ 쿠폰 선택 핸들러
    const handleCouponSelect = (e) => {
        const couponId = Number(e.target.value);
        const selected = availableCoupons.find(coupon => coupon.couponId === couponId);

        if (selected) {
            const discount = selected.discountRate > 0
                ? ((orderData.totalSum - orderData.usePoint) * (selected.discountRate) / 100)
                : selected.discountAmount;

            setSelectedCoupon(selected);
            setDiscountAmount(discount);
        } else {
            setSelectedCoupon(null);
            setDiscountAmount(0);
        }
    };

    console.log("총 결제금액 : " + finalPrice);

    if (orderInfo === undefined) {

        return (
            <div className="order-payment-container">
                <h1 className="order-title">주문 결제</h1>

                {/* ✅ 주문 상품 정보 */}
                <div className="order-top">
                    <div className="order-section">
                        <h2 className="section-title">주문상품</h2>
                        <div className="order-product-list">
                            {orderData.orderItems.map((item) => (
                                <div className="product-item" key={item.id}>
                                    <img src={item.imageUrl} className="order-product-img" alt="상품이미지" />
                                    <div className="product-info">
                                        <h3>{item.name}</h3>
                                        <p>{item.subscriptionPeriod}개월 * {item.count} 개</p>
                                        <p>{item.orderPrice.toLocaleString()}원</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ✅ 주문 요약 */}
                    <div className="order-summary">
                        <h2 className="summary-title">주문금액</h2>
                        <div className="summary-details">
                            <p>총 상품 금액 <span>{firstPrice}원</span></p>
                        </div>
                        <hr />
                        <div className="total-payment">
                            <h3>결제 예상 금액</h3>
                            <h3 className="final-price">{finalPrice.toLocaleString()} 원</h3>
                        </div>
                    </div>
                </div>

                {/* ✅ 포인트 & 쿠폰 적용 */}
                <div className="discount-section">
                    <label>포인트 사용 (최대 {maxPoints.toLocaleString()}P 사용 가능)</label>
                    <input
                        type="text"
                        value={usedPoints}
                        onChange={handlePointChange}
                        onBlur={handlePointChangeDigit}
                        placeholder={`최대 ${maxPoints.toLocaleString()}P 사용 가능`}
                    />

                    <label>쿠폰 적용</label>
                    { availableCoupons !== "noData" ? (
                        <select onChange={handleCouponSelect} value={selectedCoupon?.couponId || ""}>
                            <option value="">선택 안 함</option>
                            {availableCoupons.map(coupon => (
                                <option key={coupon.couponId} value={coupon.couponId}>
                                    {coupon.name} ({coupon.discountRate ? `${coupon.discountRate}% 할인` : `${coupon.discountAmount.toLocaleString()}원 할인`})
                                </option>
                            ))}
                        </select>
                    ) : (
                        <select onChange={handleCouponSelect} value="">
                            <option value="">사용 가능한 쿠폰이 없습니다.</option>
                        </select>
                    )
                    }

                </div>

                {/* ✅ 결제수단 선택 */}
                <div className="payment-method">
                    <div className="payment-header">
                        <h2 className="section-title">결제수단</h2>
                    </div>

                    <div className="payment-options">
                        {["간편결제", "무통장 입금"].map((method) => (
                            <button
                                key={method}
                                className={`payment-option ${selectedPayment === method ? "selected" : ""}`}
                                onClick={() => handlePaymentSelect(method)}
                            >
                                {selectedPayment === method && <span className="check-icon">✔</span>}
                                {method}
                            </button>
                        ))}
                    </div>

                    {/* ✅ 무통장 입금 시 이메일 입력 */}
                    {showEmailInput && (
                        <div className="email-input-section">
                            <label htmlFor="email">알림 받을 이메일 주소</label>
                            <input
                                type="email"
                                id="email"
                                className="email-input"
                                placeholder="example@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            {emailError && <p className="error-text">{emailError}</p>}
                        </div>
                    )}
                </div>

                {/* ✅ 무통장 입금 팝업 */}
                <PaymentPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />

                {/* ✅ 결제 버튼 */}
                <div className="cart-footer">
                    <p>주문 내용을 확인하였으며, 정보 제공 등에 동의합니다</p>
                    {selectedPayment === "무통장 입금" ? (
                        <button onClick={handleBankTransfer}>{finalPrice.toLocaleString()}원 결제</button>
                    ) : (
                        <button onClick={handleCheckout}>{finalPrice.toLocaleString()}원 결제</button>
                    )}
                </div>
            </div>
        );
    }
    else {
        if (!orderInfo) {
            toast("결제할 주문 정보가 없습니다.");
            navigate("/cart");
            return null;
        }

        console.log("주문 정보:", orderInfo);

        return (
            <div className="order-payment-container">
                <h1 className="order-title">주문 결제</h1>

                {/* ✅ 주문 상품 정보 표시 */}
                <div className="order-top">
                    <div className="order-section">
                        <h2 className="section-title">주문 상품</h2>
                        <div className="product-list">
                            <div className="product-item">
                                <img src={orderInfo.itemImageUrl} className="order-product-img" alt="상품이미지"/>
                                <div className="product-info">
                                    <h3>{orderInfo.itemName}</h3>
                                    <p>{orderInfo.subscriptionPeriod}개월 * {orderInfo.quantity} 개</p>
                                    <p>{orderInfo.finalPrice.toLocaleString()}원</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ✅ 주문 요약 */}
                <div className="order-summary">
                    <h2 className="summary-title">주문금액</h2>
                    <div className="summary-details">
                        <p>총 상품 금액 <span>{orderInfo.finalPrice.toLocaleString()}원</span></p>
                    </div>
                    <hr />
                    <div className="total-payment">
                        <h3>결제 예상 금액</h3>
                        <h3 className="final-price">{finalPrice.toLocaleString()} 원</h3>
                    </div>
                </div>

                {/* ✅ 결제수단 선택 */}
                <div className="payment-method">
                    <h2 className="section-title">결제수단</h2>
                    <div className="payment-options">
                        {["간편 결제", "무통장 입금"].map((method) => (
                            <button
                                key={method}
                                className={`payment-option ${selectedPayment === method ? "selected" : ""}`}
                                onClick={() => handlePaymentSelect(method)}
                            >
                                {selectedPayment === method && <span className="check-icon">✔</span>}
                                {method}
                            </button>
                        ))}
                    </div>

                    {/* ✅ 무통장 입금 시 이메일 입력 */}
                    {showEmailInput && (
                        <div className="email-input-section">
                            <label htmlFor="email">알림 받을 이메일</label>
                            <input
                                type="email"
                                id="email"
                                className="email-input"
                                placeholder="example@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            {emailError && <p className="error-text">{emailError}</p>}
                        </div>
                    )}
                </div>

                {/* ✅ 결제 버튼 */}
                <div className="cart-footer">
                    <p>주문 내용을 확인하였으며, 정보 제공 등에 동의합니다</p>
                    <button onClick={handleCheckoutOrderExpress}>{orderInfo.finalPrice.toLocaleString()}원 결제</button>
                </div>
            </div>
        );
    }

};

export default OrderPayment;
